import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  Check,
  RotateCcw,
  Paperclip,
  Send,
  X,
  CalendarDays,
  Table2,
} from "lucide-react";
import { apiUrl } from "../../config/environment";
import { features } from "../../config/constants/features";
import type { Lookups, PageResult, Row } from "../../config/types";
import { useQuery } from "../../hooks/queries/useQuery";
import { useAuth } from "../../store/AuthContext";
import { api } from "../../services/api";
import { display } from "../../utils/format";
import {
  Badge,
  Empty,
  ErrorNotice,
  Loading,
  Modal,
} from "../../components/common/Primitives";
import RecordForm from "../../components/common/RecordForm";
import StyledSelect from "../../components/common/StyledSelect";
import ReservationCalendar from "../meeting-reservations/ReservationCalendar";
import InterviewCalendar from "../interviews/InterviewCalendar";

function SelectionCheckbox({
  checked,
  mixed = false,
  label,
  disabled = false,
  onChange,
}: {
  checked: boolean;
  mixed?: boolean;
  label: string;
  disabled?: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return (
    <input
      ref={ref}
      className="table-selection-checkbox"
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onChange={onChange}
    />
  );
}

export default function RecordsPage({ featureKey }: { featureKey: string }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const feature = features.find((f) => f.key === featureKey)!;
  const allowedToWrite = feature.writeRoles || feature.roles;
  const canWrite =
    allowedToWrite.includes("*") || allowedToWrite.includes(user!.role);
  const canRoute =
    feature.key === "correspondence" && user!.role === "Direction";
  const canComplete =
    feature.key === "interviews" && user!.role === "Assistente";
  const permanentlyDeletesReservations =
    feature.key === "meeting-reservations" && user!.role === "Admin";
  const canEdit = (row: Row) =>
    canWrite &&
    !(feature.key === "meeting-reservations" && row.status === "Cancelled") &&
    !(feature.key === "interviews" && row.done) &&
    (feature.key !== "meeting-reservations" ||
      row.userId === user!.id ||
      user!.role === "Admin");
  const canDelete = (row: Row) =>
    feature.key === "meeting-reservations"
      ? canWrite &&
        (row.userId === user!.id || user!.role === "Admin") &&
        (row.status !== "Cancelled" || user!.role === "Admin")
      : canWrite;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [view, setView] = useState<"table" | "calendar">(
    ["meeting-reservations", "interviews"].includes(feature.key)
      ? "calendar"
      : "table",
  );
  const hasCalendar = ["meeting-reservations", "interviews"].includes(
    feature.key,
  );
  const calendarVisible = hasCalendar && view === "calendar";
  const [sort, setSort] = useState(feature.defaultSort || "createdAt");
  const [order, setOrder] = useState("DESC");
  const [editing, setEditing] = useState<Row | "new" | null>(
    canWrite && params.has("new") ? "new" : null,
  );
  const [reservationDate, setReservationDate] = useState("");
  const [repeat, setRepeat] = useState(false);
  const [routing, setRouting] = useState(false);
  const [deleting, setDeleting] = useState<Row>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 4500);
    return () => clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    setSelectedIds(new Set());
  }, [feature.key, page, limit, query]);
  const filters = new URLSearchParams({
    q: query,
    page: String(page),
    limit: String(limit),
    sort,
    order,
  });
  const result = useQuery<PageResult>("/" + feature.key + "?" + filters);
  const lookups = useQuery<Lookups>("/lookups");
  const lookup = lookups.data || {
    departments: [],
    employees: [],
    users: [],
    meetingRooms: [],
    floors: [],
    roles: [],
  };
  function close() {
    setEditing(null);
    setReservationDate("");
    setRepeat(false);
    setRouting(false);
    if (params.has("new")) setParams({}, { replace: true });
  }
  function saved() {
    close();
    result.reload();
    lookups.reload();
    setNotice("Record saved successfully.");
  }
  async function complete(row: Row) {
    setBusy(true);
    setError("");
    try {
      await api("/interviews/" + row.id + "/complete", {
        method: "POST",
        body: "{}",
      });
      result.reload();
      setNotice("Status updated.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function toggleEmployee(row: Row) {
    setBusy(true);
    setError("");
    const active = row.active === false;
    try {
      await api("/employees/" + row.id, {
        method: "PATCH",
        body: JSON.stringify({ active }),
      });
      result.reload();
      lookups.reload();
      setNotice(active ? "Employee activated." : "Employee deactivated.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    setError("");
    try {
      await api("/" + feature.key + "/" + deleting!.id, { method: "DELETE" });
      setDeleting(undefined);
      if (result.data?.items.length === 1 && page > 1) setPage(page - 1);
      else result.reload();
      setNotice(
        feature.key === "meeting-reservations" &&
          !permanentlyDeletesReservations
          ? "Reservation cancelled."
          : "Record deleted.",
      );
    } catch (e) {
      setError((e as Error).message);
      setDeleting(undefined);
    } finally {
      setBusy(false);
    }
  }
  async function removeSelected() {
    const ids = [...selectedIds];
    setBusy(true);
    setError("");
    try {
      await api("/employees/bulk", {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      setBulkDeleting(false);
      setSelectedIds(new Set());
      if (ids.length === result.data?.items.length && page > 1)
        setPage(page - 1);
      else result.reload();
      lookups.reload();
      setNotice("Selected employees deleted.");
    } catch (e) {
      setError((e as Error).message);
      setBulkDeleting(false);
    } finally {
      setBusy(false);
    }
  }
  const sortKey = (key: string) =>
      key === "person" ? "firstName" : key === "interviewStatus" ? "done" : key === "managedDepartmentIds" ? "isManager" : key === "departmentIds" ? "departmentId" : key === "employeeIds" ? "employeeId" : key;
  const columns =
    ["departments", "floors"].includes(feature.key)
      ? [
          {
            key: i18n.resolvedLanguage === "fr" ? "nameFr" : "name",
            label: feature.key === "departments" ? "Department" : "Floor",
          },
          ...(feature.key === "floors"
            ? [{ key: "active", label: "Available" }]
            : []),
          { key: "createdAt", label: "Created" },
        ]
      : feature.columns;
  const employeeSelection = feature.key === "employees" && user!.role === "Admin";
  const selectableRows = (result.data?.items || []).filter(
    (row) => row.userId !== user!.id,
  );
  const allRowsSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedIds.has(row.id));
  const someRowsSelected = selectableRows.some((row) => selectedIds.has(row.id));
  function toggleSelection(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function togglePageSelection() {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allRowsSelected)
        selectableRows.forEach((row) => next.delete(row.id));
      else selectableRows.forEach((row) => next.add(row.id));
      return next;
    });
  }
  return (
    <>
      <div className="page-heading">
        <div>
          <h1>{t(feature.title)}</h1>
          <p>{t(feature.description)}</p>
        </div>
        {canWrite && (
          <button className="button primary" onClick={() => { setReservationDate(""); setEditing("new"); }}>
            <Plus size={18} />
            {t(`New ${feature.singular}`)}
          </button>
        )}
      </div>
      {notice && (
        <div className="toast" role="status">
          <Check size={17} />
          {t(notice)}
        </div>
      )}
      {error && <ErrorNotice message={error} />}
      {lookups.error && (
        <ErrorNotice message={lookups.error} retry={lookups.reload} />
      )}
      <section className="panel records-panel">
        <div className="panel-heading">
          {hasCalendar && (
            <div className="reservation-view-switch" role="group" aria-label={t(feature.key === "interviews" ? "Interview view" : "Reservation view")}>
              <button className={"button " + (view === "calendar" ? "primary" : "secondary")} aria-pressed={view === "calendar"} onClick={() => setView("calendar")}>
                <CalendarDays size={17} />
                {t("Calendar")}
              </button>
              <button className={"button " + (view === "table" ? "primary" : "secondary")} aria-pressed={view === "table"} onClick={() => setView("table")}>
                <Table2 size={17} />
                {t("Table")}
              </button>
            </div>
          )}
          <div className="title-with-count">
            <h2>
              {t(
                `All ${feature.key === "users" ? "users" : feature.title.toLowerCase()}`,
              )}
            </h2>
            <span className="count-chip">{result.data?.total ?? "—"}</span>
          </div>
        </div>
        <div className="table-toolbar">
          <label className="search-input">
            <Search size={18} />
            <input
              aria-label={t("Search " + feature.title.toLowerCase())}
              placeholder={t("Search " + feature.title.toLowerCase() + "…")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          {!calendarVisible && (
            <div className="table-toolbar-actions">
              {employeeSelection && selectedIds.size > 0 && (
                <button
                  type="button"
                  className="button destructive compact"
                  disabled={busy}
                  onClick={() => setBulkDeleting(true)}
                >
                  <Trash2 size={15} />
                  {t("Delete selected ({{count}})", {
                    count: selectedIds.size,
                  })}
                </button>
              )}
              <label>
                {t("Rows per page")}{" "}
                <StyledSelect
                  label={t("Rows per page")}
                  value={String(limit)}
                  options={[10, 25, 50, 100].map((number) => ({
                    value: String(number),
                    label: String(number),
                  }))}
                  onChange={(value) => {
                    setLimit(Number(value));
                    setPage(1);
                  }}
                />
              </label>
            </div>
          )}
        </div>
        {calendarVisible && feature.key === "meeting-reservations" ? (
          <ReservationCalendar
            query={query}
            revision={result.data}
            lookups={lookup}
            canCreate={canWrite}
            canEdit={canEdit}
            canDelete={canDelete}
            deletePermanently={permanentlyDeletesReservations}
            onCreate={(date) => {
              setReservationDate(date);
              setEditing("new");
            }}
            onEdit={setEditing}
            onDelete={setDeleting}
          />
        ) : calendarVisible && feature.key === "interviews" ? (
          <InterviewCalendar
            query={query}
            revision={result.data}
            lookups={lookup}
            canCreate={canWrite}
            canEdit={canEdit}
            canDelete={canDelete}
            canComplete={canComplete}
            onCreate={(date) => {
              setReservationDate(date);
              setEditing("new");
            }}
            onEdit={setEditing}
            onDelete={setDeleting}
            onComplete={complete}
          />
        ) : result.error ? (
          <ErrorNotice message={result.error} retry={result.reload} />
        ) : result.loading ? (
          <Loading />
        ) : !result.data?.items.length ? (
          <Empty
            title={
              query
                ? t("No matching records")
                : t(`No ${feature.title.toLowerCase()} yet`)
            }
          >
            {query
              ? t("Try a different search.")
              : canWrite
                ? t(`Add your first ${feature.singular} to get started.`)
                : ""}
          </Empty>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  {employeeSelection && (
                    <th className="selection-column">
                      <SelectionCheckbox
                        checked={allRowsSelected}
                        mixed={someRowsSelected && !allRowsSelected}
                        disabled={!selectableRows.length || busy}
                        label={t("Select all employees on this page")}
                        onChange={togglePageSelection}
                      />
                    </th>
                  )}
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      aria-sort={
                        sort === sortKey(c.key)
                          ? order === "ASC"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <button
                        className="text-button"
                        onClick={() => {
                          setSort(sortKey(c.key));
                          setOrder(
                            sort === sortKey(c.key) && order === "ASC"
                              ? "DESC"
                              : "ASC",
                          );
                          setPage(1);
                        }}
                      >
                        {t(c.label)} <ArrowUpDown size={12} />
                      </button>
                    </th>
                  ))}
                  <th className="actions-heading">{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {result.data.items.map((row) => (
                  <tr
                    key={row.id}
                    className={selectedIds.has(row.id) ? "selected-row" : ""}
                  >
                    {employeeSelection && (
                      <td className="selection-column">
                        <SelectionCheckbox
                          checked={selectedIds.has(row.id)}
                          disabled={row.userId === user!.id || busy}
                          label={t("Select {{name}}", {
                            name: String(row.fullName || ""),
                          })}
                          onChange={() => toggleSelection(row.id)}
                        />
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.key}>
                        {feature.key === "employees" && c.key === "active" ? (
                          <button
                            type="button"
                            className={"employee-status-toggle " + (row.active === false ? "inactive" : "active")}
                            role="switch"
                            aria-checked={row.active !== false}
                            aria-label={t(row.active === false ? "Activate employee" : "Deactivate employee")}
                            disabled={busy}
                            onClick={() => toggleEmployee(row)}
                          >
                            <span className="toggle-control" aria-hidden="true">
                              <span />
                            </span>
                            {t(row.active === false ? "Inactive" : "Active")}
                          </button>
                        ) : ["interviewStatus", "status", "role"].includes(
                          c.key,
                        ) ? (
                          <Badge>{display(row, c.key, lookup)}</Badge>
                        ) : (
                          display(row, c.key, lookup)
                        )}
                      </td>
                    ))}
                    <td>
                      <div className="row-actions">
                        {row.attachmentId && (
                          <a
                            className="icon-button"
                            aria-label={t("Download attachment")}
                            href={apiUrl("/files/" + row.attachmentId)}
                          >
                            <Paperclip size={15} />
                          </a>
                        )}
                        {feature.key === "visits" && canWrite && (
                          <button
                            className="icon-button"
                            title={t("Return visit")}
                            aria-label={t("Register return visit")}
                            onClick={() => {
                              setRepeat(true);
                              setEditing(row);
                            }}
                          >
                            <RotateCcw size={15} />
                          </button>
                        )}
                        {canComplete && !row.done && (
                          <button
                            className="icon-button"
                            disabled={busy}
                            aria-label={t("Mark completed")}
                            onClick={() => complete(row)}
                          >
                            <Check size={15} />
                          </button>
                        )}
                        {canRoute && (
                          <button
                            className="icon-button"
                            aria-label={t(
                              row.routed
                                ? "Resend correspondence"
                                : "Route correspondence",
                            )}
                            onClick={() => {
                              if (
                                row.routed &&
                                !window.confirm(
                                  t("Send another correspondence email?"),
                                )
                              )
                                return;
                              setRouting(true);
                              setEditing(row);
                            }}
                          >
                            <Send size={15} />
                          </button>
                        )}
                        {canEdit(row) && (
                          <button
                            className="icon-button"
                            aria-label={t(`Edit ${feature.singular}`)}
                            onClick={() => setEditing(row)}
                          >
                            <Pencil size={15} />
                          </button>
                        )}
                        {canDelete(row) && (
                          <button
                            className="icon-button danger"
                            aria-label={t(feature.key === "meeting-reservations" ? (permanentlyDeletesReservations ? "Delete meeting room reservation" : "Cancel reservation") : `Delete ${feature.singular}`)}
                            onClick={() => setDeleting(row)}
                          >
                            {feature.key === "meeting-reservations" && !permanentlyDeletesReservations ? <X size={15} /> : <Trash2 size={15} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!calendarVisible && <div className="table-footer">
          <span>
            {result.data?.total
              ? t("{{from}}–{{to}} of {{count}} records", {
                  from: (page - 1) * limit + 1,
                  to: Math.min(page * limit, result.data.total),
                  count: result.data.total,
                })
              : t("0 records")}
          </span>
          <div className="pagination">
            <button
              className="icon-button"
              aria-label={t("Previous page")}
              disabled={page === 1 || result.loading}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft size={17} />
            </button>
            <span>
              {t("Page {{page}} of {{total}}", {
                page,
                total: Math.max(
                  1,
                  Math.ceil((result.data?.total || 0) / limit),
                ),
              })}
            </span>
            <button
              className="icon-button"
              aria-label={t("Next page")}
              disabled={
                page * limit >= (result.data?.total || 0) || result.loading
              }
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight size={17} />
            </button>
          </div>
        </div>}
      </section>
      {editing && (
        <RecordForm
          feature={feature}
          row={editing === "new" ? undefined : editing}
          repeat={repeat}
          routing={routing}
          initialValues={reservationDate ? { date: reservationDate } : undefined}
          lookups={lookup}
          onClose={close}
          onSaved={saved}
        />
      )}
      {deleting && (
        <Modal
          title={t(feature.key === "meeting-reservations" ? (permanentlyDeletesReservations ? "Delete meeting room reservation?" : "Cancel reservation?") : `Delete ${feature.singular}?`)}
          subtitle={t(
            feature.key === "meeting-reservations" && !permanentlyDeletesReservations ? "The reservation will remain visible as Cancelled and the room will become available." : "This permanently removes the record. This action cannot be undone.",
          )}
          onClose={() => {
            if (!busy) setDeleting(undefined);
          }}
        >
          <div className="modal-actions">
            <button
              className="button secondary"
              disabled={busy}
              onClick={() => setDeleting(undefined)}
            >
              {t("Cancel")}
            </button>
            <button
              className="button destructive"
              disabled={busy}
              onClick={remove}
            >
              {busy ? t("Saving…") : t(feature.key === "meeting-reservations" ? (permanentlyDeletesReservations ? "Delete meeting room reservation" : "Cancel reservation") : "Delete record")}
            </button>
          </div>
        </Modal>
      )}
      {bulkDeleting && (
        <Modal
          title={t("Delete selected employees?")}
          subtitle={t(
            "This permanently removes {{count}} selected employees and disables their linked accounts. This action cannot be undone.",
            { count: selectedIds.size },
          )}
          onClose={() => {
            if (!busy) setBulkDeleting(false);
          }}
        >
          <div className="modal-actions">
            <button
              className="button secondary"
              disabled={busy}
              onClick={() => setBulkDeleting(false)}
            >
              {t("Cancel")}
            </button>
            <button
              className="button destructive"
              disabled={busy}
              onClick={removeSelected}
            >
              {busy
                ? t("Saving…")
                : t("Delete {{count}} employees", {
                    count: selectedIds.size,
                  })}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
